import React, { useEffect, useState, useMemo, useCallback } from "react";
import Layout from "@/components/Layout";
import { PageHeader } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import {
    apiListProjects,
    apiListShifts,
    apiCreateShift,
    apiUpdateShift,
    apiDeleteShift,
    formatApiError,
} from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, AlertCircle, Clock3 } from "lucide-react";

const PRESETS = [
    { name: "Morning", start_time: "06:00", end_time: "15:00" },
    { name: "General", start_time: "09:00", end_time: "18:00" },
    { name: "Afternoon", start_time: "14:00", end_time: "23:00" },
    { name: "Night", start_time: "22:00", end_time: "07:00" },
];

function fmt(t) {
    // "HH:MM" -> "6:00 AM"
    if (!t || !t.includes(":")) return t;
    const [hh, mm] = t.split(":").map((n) => parseInt(n, 10));
    const period = hh >= 12 ? "PM" : "AM";
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${String(mm).padStart(2, "0")} ${period}`;
}

export default function AdminShifts() {
    const [projects, setProjects] = useState([]);
    const [projectId, setProjectId] = useState("");
    const [shifts, setShifts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const loadProjects = useCallback(async () => {
        try {
            const p = await apiListProjects();
            setProjects(p);
            if (!projectId && p.length > 0) {
                const firstActive = p.find((x) => x.active) || p[0];
                setProjectId(firstActive.project_id);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }, [projectId]);

    const loadShifts = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            setShifts(await apiListShifts(projectId));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        loadProjects();
    }, [loadProjects]);

    useEffect(() => {
        if (projectId) loadShifts();
    }, [projectId, loadShifts]);

    const currentProject = useMemo(
        () => projects.find((p) => p.project_id === projectId),
        [projects, projectId],
    );

    async function remove(s) {
        if (!window.confirm(`Delete shift "${s.name}"?`)) return;
        try {
            await apiDeleteShift(s.shift_id);
            toast.success("Shift deleted");
            loadShifts();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    return (
        <Layout active="shifts">
            <PageHeader
                title="Shifts"
                subtitle="Define shift windows for each project."
                actions={
                    <Button
                        onClick={() => setAddOpen(true)}
                        disabled={!projectId}
                        data-testid="add-shift-btn"
                        className="h-9 bg-[#0072BC] hover:bg-[#005a94] text-white"
                    >
                        <Plus className="h-4 w-4 mr-1.5" />
                        Add shift
                    </Button>
                }
            />

            {projects.length === 0 ? (
                <div
                    className="bg-white border border-ntt-border rounded-xl p-10 text-center"
                    data-testid="no-projects-hint"
                >
                    <div className="text-sm text-ntt-muted mb-3">
                        Add a project first, then define its shifts.
                    </div>
                </div>
            ) : (
                <>
                    <div className="bg-white border border-ntt-border rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3">
                        <Label className="text-[12px] text-ntt-muted">
                            Project
                        </Label>
                        <Select
                            value={projectId}
                            onValueChange={setProjectId}
                        >
                            <SelectTrigger
                                className="h-10 w-[280px]"
                                data-testid="project-picker-trigger"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {projects.map((p) => (
                                    <SelectItem
                                        key={p.project_id}
                                        value={p.project_id}
                                        data-testid={`project-picker-option-${p.project_id}`}
                                    >
                                        {p.name} · {p.code}
                                        {!p.active && " (inactive)"}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {currentProject && !currentProject.active && (
                            <span className="text-[12px] text-red-600">
                                Project is inactive
                            </span>
                        )}
                    </div>

                    <div
                        className="bg-white border border-ntt-border rounded-xl overflow-hidden"
                        data-testid="shifts-table"
                    >
                        <div className="grid grid-cols-[minmax(180px,1.4fr)_120px_120px_140px_140px] gap-4 px-5 py-3 border-b border-ntt-border bg-[#F4F6F9] text-[11px] font-medium uppercase tracking-wider text-ntt-muted">
                            <div>Name</div>
                            <div>Start</div>
                            <div>End</div>
                            <div>Window</div>
                            <div className="text-right">Actions</div>
                        </div>
                        {loading ? (
                            <div className="p-8 text-center text-sm text-ntt-muted">
                                Loading shifts…
                            </div>
                        ) : shifts.length === 0 ? (
                            <div className="p-10 text-center text-sm text-ntt-muted">
                                No shifts yet for this project. Try adding a
                                preset like Morning, General, Afternoon or
                                Night.
                            </div>
                        ) : (
                            shifts.map((s) => (
                                <div
                                    key={s.shift_id}
                                    data-testid={`shift-row-${s.shift_id}`}
                                    className="grid grid-cols-[minmax(180px,1.4fr)_120px_120px_140px_140px] gap-4 px-5 py-3 border-b border-ntt-border last:border-0 items-center hover:bg-[#FBFCFE]"
                                >
                                    <div className="text-[13px] font-medium text-[#070F26] flex items-center gap-2">
                                        <span className="h-7 w-7 rounded-md bg-[#DBEAF6] text-[#0072BC] flex items-center justify-center">
                                            <Clock3 className="h-3.5 w-3.5" />
                                        </span>
                                        {s.name}
                                    </div>
                                    <div className="text-[13px] font-mono">
                                        {s.start_time}
                                    </div>
                                    <div className="text-[13px] font-mono">
                                        {s.end_time}
                                    </div>
                                    <div className="text-[12px] text-ntt-muted">
                                        {fmt(s.start_time)} – {fmt(s.end_time)}
                                    </div>
                                    <div className="flex items-center justify-end gap-1">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setEditing(s)}
                                            data-testid={`edit-shift-${s.shift_id}`}
                                            className="h-8 text-[12px]"
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => remove(s)}
                                            data-testid={`delete-shift-${s.shift_id}`}
                                            className="h-8 text-[12px] text-red-600 hover:text-red-700"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}

            <ShiftFormDialog
                open={addOpen}
                onOpenChange={setAddOpen}
                mode="create"
                projectId={projectId}
                onDone={loadShifts}
            />
            <ShiftFormDialog
                open={!!editing}
                onOpenChange={(v) => !v && setEditing(null)}
                mode="edit"
                shift={editing}
                onDone={() => {
                    setEditing(null);
                    loadShifts();
                }}
            />
        </Layout>
    );
}

function ShiftFormDialog({ open, onOpenChange, mode, projectId, shift, onDone }) {
    const [name, setName] = useState(shift?.name || "");
    const [startTime, setStartTime] = useState(shift?.start_time || "09:00");
    const [endTime, setEndTime] = useState(shift?.end_time || "18:00");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName(shift?.name || "");
            setStartTime(shift?.start_time || "09:00");
            setEndTime(shift?.end_time || "18:00");
            setError("");
        }
    }, [open, shift]);

    function applyPreset(p) {
        setName(p.name);
        setStartTime(p.start_time);
        setEndTime(p.end_time);
    }

    async function submit(e) {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            if (mode === "create") {
                await apiCreateShift(projectId, name.trim(), startTime, endTime);
                toast.success("Shift added");
            } else {
                await apiUpdateShift(shift.shift_id, {
                    name: name.trim(),
                    start_time: startTime,
                    end_time: endTime,
                });
                toast.success("Shift updated");
            }
            onOpenChange(false);
            onDone?.();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[520px]" data-testid="shift-dialog">
                <DialogHeader>
                    <DialogTitle>
                        {mode === "create" ? "Add shift" : "Edit shift"}
                    </DialogTitle>
                    <DialogDescription>
                        Set a name and 24-hour times. Overnight shifts (e.g.
                        Night 22:00 – 07:00) are fine.
                    </DialogDescription>
                </DialogHeader>

                {mode === "create" && (
                    <div>
                        <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                            Quick presets
                        </Label>
                        <div className="flex flex-wrap gap-1.5" data-testid="shift-presets">
                            {PRESETS.map((p) => (
                                <button
                                    key={p.name}
                                    type="button"
                                    onClick={() => applyPreset(p)}
                                    data-testid={`preset-${p.name.toLowerCase()}`}
                                    className="h-8 px-3 rounded-full text-[12px] border border-ntt-border bg-white hover:border-[#0072BC] hover:text-[#0072BC] transition-colors"
                                >
                                    {p.name} · {fmt(p.start_time)}–{fmt(p.end_time)}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <form onSubmit={submit} className="space-y-4" data-testid="shift-form">
                    <div>
                        <Label htmlFor="sn" className="text-[12px] text-ntt-muted mb-1.5 block">
                            Name
                        </Label>
                        <Input
                            id="sn"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Morning, Weekend, Bridge"
                            data-testid="shift-name-input"
                            className="h-10"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="st" className="text-[12px] text-ntt-muted mb-1.5 block">
                                Start time
                            </Label>
                            <Input
                                id="st"
                                type="time"
                                required
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                data-testid="shift-start-input"
                                className="h-10 font-mono"
                            />
                        </div>
                        <div>
                            <Label htmlFor="et" className="text-[12px] text-ntt-muted mb-1.5 block">
                                End time
                            </Label>
                            <Input
                                id="et"
                                type="time"
                                required
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                data-testid="shift-end-input"
                                className="h-10 font-mono"
                            />
                        </div>
                    </div>
                    <p className="text-[11px] text-ntt-muted">
                        Window: {fmt(startTime)} – {fmt(endTime)}
                    </p>
                    {error && (
                        <div
                            data-testid="shift-form-error"
                            className="flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                        >
                            <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="shift-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            data-testid="shift-save-btn"
                            className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                        >
                            {saving ? "Saving…" : mode === "create" ? "Add shift" : "Save"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
